import { useEffect, useRef, TransitionEvent, useState } from 'react';
import classNames from 'classnames';

import { getMyRewards } from '@/arise/api';
import { Reward } from '@/models/misc';
import { sendEventV2 } from '@/utils/analytics';
import { pathToURL } from '@/utils/urls';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { useAudio } from '@/hooks/audio';
import { setCurrentlyViewingReward, setIsMyRewardsOpen } from '@/state/features/app';
import IconButton from '@/components/IconButton';
import LoadingScreen from '@/components/LoadingScreen';
import ItemContainer from '@/components/ItemContainer';

import * as styles from './styles.module.scss';

export default function MyRewardsDrawer() {
    const dispatch = useAppDispatch();
    const { audio } = useAudio();
    const isMyRewardsOpen = useAppSelector((state) => state.app.isMyRewardsOpen);

    const [rewardItems, setRewardItems] = useState<Reward[]>([]);
    const [rewardItemsAreLoading, setRewardItemsAreLoading] = useState<boolean>(true);

    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const loadRewards = () => {
        getMyRewards()
            .then((items) => {
                if (Array.isArray(items)) {
                    setRewardItems(items);
                } else {
                    console.error('Error loading rewards');
                }
                setRewardItemsAreLoading(false);
            })
            .catch(() => {
                setRewardItemsAreLoading(false);
                console.log('Error loading rewards');
            });
    };

    useEffect(() => {
        if (isMyRewardsOpen) {
            audio?.play('gust-open');
            // Reload data when opened
            setRewardItemsAreLoading(true);
            loadRewards();
            sendEventV2('reward_drawer_viewed');
        }
    }, [isMyRewardsOpen]);

    // Move focus to drawer when opened
    function onTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
        if (isMyRewardsOpen && e.target === e.currentTarget) {
            setTimeout(() => {
                closeButtonRef.current?.focus();
            }, 200);
        }
    }

    return (
        <div
            className={classNames(styles.MyRewardsDrawer, {
                [styles.isOpen]: isMyRewardsOpen,
            })}
            inert={!isMyRewardsOpen ? '' : undefined}
            onTransitionEnd={onTransitionEnd}
        >
            <div className={styles.scrollContainer}>
                <div className={styles.topBar}>
                    <h2 className={styles.title}>Your Rewards!</h2>
                    <IconButton
                        buttonRef={closeButtonRef}
                        iconType="close"
                        buttonStyle="round"
                        ariaLabel="Close My Rewards"
                        className={styles.closeButton}
                        onClick={() => {
                            audio?.play('gust-close');
                            dispatch(setIsMyRewardsOpen(false));
                        }}
                    />
                </div>
                <div className={styles.content}>
                    {rewardItems.length === 0 && !rewardItemsAreLoading && (
                        <p className={styles.emptyText}>No rewards earned yet</p>
                    )}
                    <ul>
                        {rewardItems.map((item, index) => {
                            return (
                                <li key={`my-reward-${index}`}>
                                    <ItemContainer
                                        title={item.name}
                                        imagePath={pathToURL(item.mainImagePath)}
                                        backgroundImagePath={''}
                                        textBackgroundImagePath={''}
                                        imageAlt={item.description}
                                        interactable={true}
                                        onSelected={() => dispatch(setCurrentlyViewingReward(item))}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                    <LoadingScreen hasBackground={false} show={rewardItems.length === 0 && rewardItemsAreLoading} />
                </div>
            </div>
        </div>
    );
}
